import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SiteProvider, useSiteContext } from './context/SiteContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Hero from './components/sections/Hero';
import FeaturedReferences from './components/sections/FeaturedReferences';
import ReferenceGrid from './components/sections/ReferenceGrid';
import BuiltBeyondPage from './components/sections/BuiltBeyondPage';
import ContactSection from './components/sections/ContactSection';
import SeoSection from './components/sections/SeoSection';
import ReferenceModal from './components/ui/ReferenceModal';
import SyncTick from './components/ui/SyncTick';
import SafeIcon from './common/SafeIcon';

const STAGES = [
  "Preparing your website reference view",
  "Getting the preview cards ready",
  "Organising website systems by relevance",
  "Finalising your browsing experience"
];

const InitialSyncOverlay = () => {
  const { initialSyncActive } = useSiteContext();
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    if (!initialSyncActive) {
      setStageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setStageIndex((prev) => (prev < STAGES.length - 1 ? prev + 1 : prev));
    }, 1100);

    return () => clearInterval(interval);
  }, [initialSyncActive]);

  if (!initialSyncActive) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[45] pointer-events-none"
      >
        <div className="absolute inset-0 bg-[#05050a]/70 backdrop-blur-xl backdrop-saturate-150" style={{ background: 'radial-gradient(circle at center, rgba(37, 99, 235, 0.18), rgba(5, 5, 10, 0.78) 45%, rgba(5, 5, 10, 0.88) 100%)' }} />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative z-10 max-w-md w-[calc(100%-2rem)] bg-[#14141d]/95 backdrop-blur-2xl border border-white/15 rounded-3xl shadow-[0_30px_120px_rgba(37,99,235,0.35)] ring-1 ring-blue-400/20 p-6 pointer-events-none"
          >
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-5">
                <div className="w-14 h-14 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <SafeIcon name="Layers" className="w-5 h-5 text-blue-400" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Preparing Your View</h3>
              <div className="h-10 flex items-center justify-center overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={stageIndex}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="text-sm text-slate-400"
                  >
                    {STAGES[stageIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>
              <div className="flex gap-1.5 mt-4">
                {[0, 1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    animate={{
                      scale: i === stageIndex ? 1.2 : 1,
                      opacity: i <= stageIndex ? 1 : 0.4
                    }}
                    transition={{ duration: 0.2 }}
                    className="w-1.5 h-1.5 rounded-full bg-blue-500"
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const ScrollControls = () => {
  const { isModalOpen } = useSiteContext();
  const [showControls, setShowControls] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [showTopPill, setShowTopPill] = useState(false);

  const handleScroll = useCallback(() => {
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;

      setShowControls(scrollY > 220);

      const atBottom = viewportHeight + scrollY >= scrollHeight - 80;
      setIsAtBottom(atBottom);
      setShowTopPill(atBottom);
    });
  }, []);

  useEffect(() => {
    const scrollListener = handleScroll;
    window.addEventListener('scroll', scrollListener, { passive: true });
    return () => window.removeEventListener('scroll', scrollListener);
  }, [handleScroll]);

  const scrollByViewport = useCallback((direction) => {
    const viewportHeight = window.innerHeight;
    const scrollAmount = viewportHeight * 0.8;
    const newScrollY = window.scrollY + (direction === 'down' ? scrollAmount : -scrollAmount);
    window.scrollTo({ top: newScrollY, behavior: 'smooth' });
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (isModalOpen) return null;

  return (
    <AnimatePresence>
      {showControls && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+1rem)] sm:bottom-6 right-4 sm:right-6 z-40 flex flex-col items-center gap-3"
        >
          <AnimatePresence>
            {showTopPill && (
              <motion.button
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                onClick={scrollToTop}
                aria-label="Take me to top"
                className="px-3 py-1.5 text-[11px] font-medium bg-white/8 backdrop-blur-xl border border-white/10 rounded-full text-slate-300 hover:text-white hover:bg-white/12 transition-all shadow-[0_4px_20px_rgba(0,0,0,0.3)] hover:shadow-[0_6px_25px_rgba(59,130,246,0.25)] focus:outline-none focus:ring-2 focus:ring-blue-400/50"
              >
                Take me to top
              </motion.button>
            )}
          </AnimatePresence>

          <div className="flex flex-col gap-2">
            <button
              onClick={() => scrollByViewport('up')}
              aria-label="Scroll up"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/8 backdrop-blur-xl border border-white/10 text-white/80 hover:text-white hover:bg-white/12 transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.3)] hover:shadow-[0_6px_25px_rgba(59,130,246,0.25)] hover:border-blue-400/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 flex items-center justify-center"
            >
              <SafeIcon name="ChevronUp" className="w-5 h-5" />
            </button>

            <button
              onClick={() => scrollByViewport('down')}
              disabled={isAtBottom}
              aria-label="Scroll down"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/8 backdrop-blur-xl border border-white/10 text-white/80 hover:text-white hover:bg-white/12 transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.3)] hover:shadow-[0_6px_25px_rgba(59,130,246,0.25)] hover:border-blue-400/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 flex items-center justify-center disabled:opacity-35 disabled:grayscale disabled:cursor-not-allowed disabled:hover:bg-white/8 disabled:hover:shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
            >
              <SafeIcon name="ChevronDown" className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

function App() {
  return (
    <SiteProvider>
      <div className="min-h-screen flex flex-col premium-scrollbar">
        <Header />
        <main className="flex-grow">
          <Hero />
          <FeaturedReferences />
          <BuiltBeyondPage />
          <ReferenceGrid />
          <SeoSection />
          <ContactSection />
        </main>
        <Footer />
        <ReferenceModal />
        <SyncTick />
        {/* The page is available immediately; CMS data refreshes silently. */}
        <ScrollControls />
      </div>
    </SiteProvider>
  );
}

export default App;